import clsx from 'clsx';
import { enqueueSnackbar } from 'notistack';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import {
  IconEye,
  IconPlus,
  IconSearch,
  IconPencil,
  IconAdjustments,
  IconHourglassEmpty,
} from '@tabler/icons-react';

import AccordionTable, {
  IAccordionColumn,
  IAccordionChildRow,
  IAccordionParentRow,
} from '@/components/AccordionTable';
import ActionDropdown from '@/components/ActionDropdown';
import Button from '@/components/Button/Button';
import EmptyState from '@/components/EmptyState';
import Input from '@/components/Input/Input';
import GameDashboardSkeleton from '@/components/SkeletonLoader/GameDashboardSkeleton';
import TagCapsule from '@/components/TagCapsule';
import { LessonStageConfig } from '@/constants/constant';
import { EContentType, ELessonStageType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import CreateLessonModal from '@/pages/common/createLesson/CreateLessonModal';
import { IContentPair } from '@/pages/common/gameContent/EditGameContent';
import {
  getLessonGameList,
  getLessonAvailableGames,
} from '@/service/gameService';
import { addLessonGame } from '@/service/lessonService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { ILessonGame, ILessonGameQuarter } from '@/types/lesson';

import EditGameModal, { IEditGameSubmitPayload } from './EditGameModal';
import FilterDrawer, { IGameDashboardFilters } from './FilterDrawer';
import GameDetailModal from './GameDetailModal';

const PAGE_SIZE = 10;

type TCrumbLabels = {
  classes: string;
  lessons: string;
  addGame: string;
  createLesson: string;
};

const buildClassRouteCrumbs = (
  pathname: string,
  state: Record<string, string> | null,
  isAddGameMode: boolean,
  labels: TCrumbLabels
) => {
  const classId = /\/classes\/([^/]+)/.exec(pathname)?.[1];
  const classesBase = '/teacher/classes';
  const classLink = classId ? `${classesBase}/${classId}` : classesBase;
  const lastLabel = isAddGameMode ? labels.addGame : labels.createLesson;
  return [
    { label: labels.classes, to: classesBase },
    { label: state?.className || '...', to: classLink },
    ...(isAddGameMode && state?.lessonTitle
      ? [{ label: state.lessonTitle }]
      : []),
    { label: lastLabel },
  ];
};

const buildAddGameCrumbs = (
  pathname: string,
  state: Record<string, string> | null,
  isTeacher: boolean,
  labels: TCrumbLabels
) => {
  const basePath = isTeacher ? '/teacher/lessons' : '/lessons';
  const lessonId = /\/lessons\/([^/]+)/.exec(pathname)?.[1];
  const lessonLink = lessonId ? `${basePath}/${lessonId}` : basePath;
  return [
    { label: labels.lessons, to: basePath },
    { label: state?.lessonTitle || '...', to: lessonLink },
    { label: labels.addGame },
  ];
};

const GameDashboard = () => {
  const { formatText } = useFormatText();
  const { id: classIdParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsBreadcrumb } = usePageTitle();
  const language = useSettingsStore((state) => state.settings.language);

  const isAddGameMode = location.pathname.includes('/add-game');
  const isCreateFromClass = location.pathname.includes('/create-lesson');
  const preselectedClassId = isCreateFromClass ? classIdParam : undefined;
  const moduleType: 'teacher' | 'school' = location.pathname.startsWith(
    '/teacher'
  )
    ? 'teacher'
    : 'school';

  const [isCreateLessonOpen, setIsCreateLessonOpen] = useState(false);
  const [createLessonGames, setCreateLessonGames] = useState<
    { game_id: string; values: string[] | null }[]
  >([]);

  const [games, setGames] = useState<ILessonGame[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedGames, setExpandedGames] = useState<Set<number>>(new Set());
  const [selectedQuarters, setSelectedQuarters] = useState<Set<string>>(
    new Set()
  );
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'' | 'ASC' | 'DESC'>('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<IGameDashboardFilters | null>(null);

  // ── Edit game modal state ────────────────────────────────────────────────
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingQuarterId, setEditingQuarterId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quarterUpdates, setQuarterUpdates] = useState<
    Record<string, { isUpdated: boolean; contentPairs: IContentPair[] }>
  >({});

  // For /lessons/:id/add-game the URL :id IS the lessonId; prefer it over state.
  // For /classes/:id/add-game the :id is the classId — lessonId must come from state.
  const isLessonAddGame =
    isAddGameMode && location.pathname.includes('/lessons/');
  const lessonId = isLessonAddGame
    ? (classIdParam ?? (location.state as { lessonId?: string })?.lessonId)
    : (location.state as { lessonId?: string })?.lessonId;

  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const { isScreenLoading, isTableLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();
  const isInitialLoad = useRef(true);

  const { register, watch } = useForm({ defaultValues: { search: '' } });
  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  // ── Breadcrumb ────────────────────────────────────────────────────────────

  const buildBreadcrumbs = () => {
    const pathname = location.pathname;
    const state = location.state as Record<string, string> | null;
    const isTeacher = pathname.startsWith('/teacher');
    const isClassRoute = pathname.includes('/classes/');
    const labels = {
      classes: formatText('teacher-classes.title'),
      lessons: formatText('sidebar.lessons'),
      addGame: formatText('create-lesson.add-game'),
      createLesson: formatText('lessons.create-lesson'),
    };

    if (isTeacher && isClassRoute) {
      return buildClassRouteCrumbs(pathname, state, isAddGameMode, labels);
    }
    if (isAddGameMode) {
      return buildAddGameCrumbs(pathname, state, isTeacher, labels);
    }
    return [
      {
        label: labels.lessons,
        to: isTeacher ? '/teacher/lessons' : '/lessons',
      },
      { label: labels.createLesson },
    ];
  };

  useEffect(() => {
    setIsBreadcrumb(buildBreadcrumbs());
    return () => setIsBreadcrumb([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, isAddGameMode]);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const setLoadingState = (loading: boolean) => {
    if (isInitialLoad.current) setScreenLoading(loading);
    else setTableLoading(loading);
  };

  const fetchGames = useCallback(async () => {
    setLoadingState(true);
    try {
      const query: Record<string, unknown> = {
        limit: PAGE_SIZE,
        offset: (currentPage - 1) * PAGE_SIZE,
      };
      if (debouncedSearch) query.search = debouncedSearch;
      if (sortField && sortDir) query[`order[${sortField}]`] = sortDir;
      if (filters?.contentType) query.content_type = filters.contentType;
      if (filters?.quarter) query.quarters = filters.quarter;
      if (filters?.stage) query.category = filters.stage;
      if (filters?.skills) {
        const arr = Array.isArray(filters.skills)
          ? filters.skills
          : [filters.skills];
        if (arr.length > 0) query.skills = arr;
      }

      const res =
        isAddGameMode && lessonId
          ? await getLessonAvailableGames(lessonId, query)
          : await getLessonGameList(query);
      setGames(res?.data ?? []);
      setTotal(res?.total ?? 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingState(false);
      if (isInitialLoad.current) isInitialLoad.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    debouncedSearch,
    sortField,
    sortDir,
    filters,
    isAddGameMode,
    lessonId,
  ]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Clear expanded rows when the displayed games change
  useEffect(() => {
    setExpandedGames(new Set());
  }, [currentPage]);

  // ── Filters ────────────────────────────────────────────────────────────────

  const handleApplyFilters = (newFilters: IGameDashboardFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleEditQuarter = (quarterId: string) => {
    setEditingQuarterId(quarterId);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditingQuarterId('');
  };

  const handleEditModalSubmit = (payload: IEditGameSubmitPayload) => {
    setQuarterUpdates((prev) => ({
      ...prev,
      [payload.quarterId]: {
        isUpdated: payload.isUpdated,
        contentPairs: payload.contentPairs,
      },
    }));
    handleEditModalClose();
  };

  // ── Submit (Add Game / Create Lesson) ─────────────────────────────────────

  const buildGamesPayload = () => {
    return Array.from(selectedQuarters).map((qId) => {
      const update = quarterUpdates[qId];
      if (update?.isUpdated) {
        return {
          game_id: qId,
          values: update.contentPairs.map((p) => p.changeTo),
        };
      }
      return { game_id: qId, values: null };
    });
  };

  const handleAddGame = async () => {
    if (!lessonId || selectedQuarters.size === 0) return;
    setIsSubmitting(true);
    try {
      const payload = { games: buildGamesPayload() };
      const res = await addLessonGame(lessonId, payload);
      if (res?.status) {
        enqueueSnackbar(res.message, { variant: 'success' });
        navigate(-1);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateLesson = () => {
    setCreateLessonGames(buildGamesPayload());
    setIsCreateLessonOpen(true);
  };

  const handleCreateLessonClose = () => {
    setIsCreateLessonOpen(false);
  };

  const handleCreateLessonSuccess = () => {
    setIsCreateLessonOpen(false);
    navigate(-1);
  };

  const isFilterApplied =
    !!filters?.contentType ||
    !!filters?.quarter ||
    !!filters?.stage ||
    (filters?.skills?.length ?? 0) > 0;

  // ── Sorting ───────────────────────────────────────────────────────────────

  const handleSort = (key: string) => {
    if (sortField !== key) {
      setSortField(key);
      setSortDir('ASC');
      return;
    }
    if (sortDir === 'ASC') setSortDir('DESC');
    else if (sortDir === 'DESC') {
      setSortField('');
      setSortDir('');
    } else setSortDir('ASC');
  };

  // ── Selection ─────────────────────────────────────────────────────────────

  const toggleExpand = (gameIdx: number) => {
    setExpandedGames((prev) => {
      const next = new Set(prev);
      if (next.has(gameIdx)) next.delete(gameIdx);
      else next.add(gameIdx);
      return next;
    });
  };

  const isGameFullySelected = (idx: number) => {
    const game = games[idx];
    return (
      game.quarters.length > 0 &&
      game.quarters.every((q) => selectedQuarters.has(q.id))
    );
  };

  const isGamePartiallySelected = (idx: number) => {
    const game = games[idx];
    const count = game.quarters.filter((q) =>
      selectedQuarters.has(q.id)
    ).length;
    return count > 0 && count < game.quarters.length;
  };

  const toggleGameSelection = (idx: number, checked: boolean) => {
    const game = games[idx];
    setSelectedQuarters((prev) => {
      const next = new Set(prev);
      game.quarters.forEach((q) => {
        if (checked) next.add(q.id);
        else next.delete(q.id);
      });
      return next;
    });
  };

  const toggleQuarterSelection = (quarterId: string, checked: boolean) => {
    setSelectedQuarters((prev) => {
      const next = new Set(prev);
      if (checked) next.add(quarterId);
      else next.delete(quarterId);
      return next;
    });
  };

  const handleViewGame = (quarterGameId: string) => {
    setSelectedGameId(quarterGameId);
    setIsViewModalOpen(true);
  };

  // ── Cell renderers ────────────────────────────────────────────────────────

  const getGameTitle = (game: ILessonGame) =>
    language === 'ar' ? game.title_ar : game.title_en;

  const renderStage = (category?: number) => {
    if (!category) return <span className="text-neutral-secondary">-</span>;
    const config = LessonStageConfig[category as ELessonStageType];
    if (!config) return <span className="text-neutral-secondary">-</span>;
    return (
      <TagCapsule
        value={formatText(config.label)}
        className={config.className}
      />
    );
  };

  const renderContentCell = (
    quarter: ILessonGameQuarter,
    colType: EContentType
  ) => {
    if (quarter.content_type !== colType || !quarter.contents?.length)
      return <span className="text-neutral-secondary">-</span>;

    const separator = language === 'ar' ? '، ' : ', ';
    const text = quarter.contents.join(separator);
    return (
      <span
        className="block truncate"
        dir={colType === EContentType.LETTERS ? 'rtl' : 'ltr'}
        title={text}
      >
        {text}
      </span>
    );
  };

  const renderSkills = (quarter: ILessonGameQuarter) => {
    if (!quarter.skills?.length)
      return <span className="text-neutral-secondary">-</span>;

    const titles = quarter.skills.map((s) =>
      language === 'ar' ? s.title_ar : s.title_en
    );
    const text = titles.join(', ');
    return (
      <span className="block truncate" title={text}>
        {text}
      </span>
    );
  };

  // ── Build accordion data ──────────────────────────────────────────────────

  const columns: IAccordionColumn[] = useMemo(
    () => [
      {
        key: 'games',
        label: formatText('create-lesson.columns.games'),
        width: 250,
        sorter: true,
        sortKey: language === 'ar' ? 'title_ar' : 'title_en',
      },
      {
        key: 'rounds',
        label: formatText('create-lesson.columns.round'),
        width: 150,
        sorter: false,
      },
      {
        key: 'letters',
        label: formatText('create-lesson.columns.letters'),
        width: 180,
        sorter: false,
      },
      {
        key: 'word',
        label: formatText('create-lesson.columns.word'),
        width: 160,
        sorter: false,
      },
      {
        key: 'sentences',
        label: formatText('create-lesson.columns.sentences'),
        width: 180,
        sorter: false,
      },
      {
        key: 'skills',
        label: formatText('create-lesson.columns.skills'),
        width: 240,
        sorter: false,
      },
      {
        key: 'category',
        label: formatText('create-lesson.columns.stage'),
        width: 130,
        sorter: false,
      },
      {
        key: 'action',
        label: formatText('create-lesson.columns.action'),
        width: 70,
        sorter: false,
      },
    ],
    [formatText, language]
  );

  const parents: IAccordionParentRow[] = useMemo(
    () =>
      games.map((game) => ({
        label: (
          <>
            <div className="flex items-center justify-center w-10 h-10 bg-white rounded-lg shadow-sm ring-1 ring-neutral-200 shrink-0">
              {game.icon ? (
                <img
                  src={game.icon}
                  alt={getGameTitle(game)}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <IconHourglassEmpty size={22} className="text-purple-900" />
              )}
            </div>
            <span
              className="text-sm font-medium text-neutral-primary truncate"
              title={getGameTitle(game)}
            >
              {getGameTitle(game)}
            </span>
          </>
        ),
        childCount: game.quarters.length,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [games, language]
  );

  const buildQuarterCells = (q: ILessonGameQuarter): IAccordionChildRow => {
    const localUpdate = quarterUpdates[q.id];
    const showUpdated = localUpdate?.isUpdated;

    return {
      id: q.id,
      label: (
        <>
          <span className="text-sm font-medium text-neutral-primary whitespace-nowrap">
            {formatText('create-lesson.quarter')} {q.quarter}
          </span>
          {showUpdated && (
            <TagCapsule
              value={formatText('lessons.details-drawer.updated')}
              className="text-brand-default bg-brand-light-50"
            />
          )}
        </>
      ),
      cells: {
        rounds: <>{q.rounds ?? '-'}</>,
        letters: renderContentCell(q, EContentType.LETTERS),
        word: renderContentCell(q, EContentType.WORDS),
        sentences: renderContentCell(q, EContentType.SENTENCES),
        skills: renderSkills(q),
        category: renderStage(q.category),
        action: (
          <div
            onClick={(e) => e.stopPropagation()}
            className="inline-block text-start"
          >
            <ActionDropdown
              actions={[
                {
                  key: 'view',
                  label: 'common.actions.view',
                  icon: <IconEye size={18} />,
                  onClick: () => handleViewGame(q.id),
                },
                ...(q.is_customizable
                  ? [
                      {
                        key: 'edit',
                        label: 'common.actions.edit',
                        icon: <IconPencil size={18} />,
                        onClick: () => handleEditQuarter(q.id),
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        ),
      },
    };
  };

  const childrenByParent: IAccordionChildRow[][] = useMemo(
    () => games.map((game) => game.quarters.map((q) => buildQuarterCells(q))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [games, language, formatText, quarterUpdates]
  );

  // ── Derived ───────────────────────────────────────────────────────────────

  const hasSelection = selectedQuarters.size > 0;
  const isEmptySearch =
    !isScreenLoading &&
    !isTableLoading &&
    games.length === 0 &&
    !!debouncedSearch;

  const isEmptyData =
    !isScreenLoading &&
    !isTableLoading &&
    games.length === 0 &&
    !debouncedSearch;

  if (isEmptyData) {
    return <EmptyState title={formatText('common.no-data-found')} />;
  }

  if (isScreenLoading) return <GameDashboardSkeleton />;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 ps-1">
        <div className="flex items-center gap-4 flex-1">
          <Input
            name="search"
            register={register}
            placeholder={formatText('create-lesson.search-placeholder')}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-70"
            inputClassName="py-3"
          />
          <div className="relative">
            <Button
              variant="outline"
              label={formatText('lessons.filter')}
              icon={<IconAdjustments size={20} />}
              className={clsx(
                'h-12',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsFilterOpen(true)}
              dataTestId="filter-button"
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>

        <div
          title={
            !hasSelection
              ? formatText('create-lesson.select-game-tooltip')
              : undefined
          }
        >
          <Button
            variant="primary"
            label={
              isAddGameMode
                ? formatText('create-lesson.add-game')
                : formatText('lessons.create-lesson')
            }
            className="h-12"
            icon={<IconPlus size={20} />}
            disabled={!hasSelection || isSubmitting}
            loading={isSubmitting}
            onClick={isAddGameMode ? handleAddGame : handleCreateLesson}
          />
        </div>
      </div>

      <div className="text-lg font-semibold leading-7 text-neutral-primary px-1">
        {formatText('create-lesson.games-list-title')}
      </div>

      {/* Accordion table */}
      <AccordionTable
        columns={columns}
        parents={parents}
        childrenByParent={childrenByParent}
        expandedRows={expandedGames}
        onToggleExpand={toggleExpand}
        selectedKeys={selectedQuarters}
        onToggleParent={toggleGameSelection}
        onToggleChild={toggleQuarterSelection}
        isParentFullySelected={isGameFullySelected}
        isParentPartiallySelected={isGamePartiallySelected}
        sortField={sortField}
        sortDirection={sortDir}
        onSort={handleSort}
        total={total}
        currentPage={currentPage}
        pageSize={PAGE_SIZE}
        setCurrentPage={setCurrentPage}
        isLoading={isTableLoading}
        isEmptySearch={isEmptySearch}
        emptyTitle={formatText('common.no-data-found')}
        emptyClassName="h-[65vh]!"
      />

      {/* Filter drawer */}
      <FilterDrawer
        isOpen={isFilterOpen}
        setIsOpen={setIsFilterOpen}
        initialFilters={filters}
        onApplyFilters={handleApplyFilters}
      />

      {/* Edit game modal */}
      <EditGameModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSubmit={handleEditModalSubmit}
        quarterId={editingQuarterId}
        existingPairs={quarterUpdates[editingQuarterId]?.contentPairs}
      />

      {/* Create lesson modal */}
      <CreateLessonModal
        isOpen={isCreateLessonOpen}
        onClose={handleCreateLessonClose}
        moduleType={moduleType}
        selectedGames={createLessonGames}
        preselectedClassId={preselectedClassId}
        onSuccess={handleCreateLessonSuccess}
      />

      <GameDetailModal
        gameId={selectedGameId}
        isOpen={isViewModalOpen}
        setIsOpen={setIsViewModalOpen}
      />
    </div>
  );
};

export default GameDashboard;
